import React, { useState, useMemo } from 'react';
import charityData from '../data/charityData';

const NONE_COUNTRY = 'None';

const CharitySelector = ({ onSelect, value }) => {
  const [selectedCountry, setSelectedCountry] = useState(value?.country || '');
  const [selectedCategory, setSelectedCategory] = useState(value?.category || '');

  const isNoneSelected = useMemo(() => {
    return String(selectedCountry || '').trim().toLowerCase() === NONE_COUNTRY.toLowerCase();
  }, [selectedCountry]);

  const countryData = useMemo(() => {
    if (isNoneSelected) return null;
    return charityData.find(c => c.country === selectedCountry);
  }, [isNoneSelected, selectedCountry]);

  const categories = useMemo(() => {
    if (!countryData) return [];
    return Object.keys(countryData.categories);
  }, [countryData]);

  const orgs = useMemo(() => {
    if (!countryData || !selectedCategory) return [];
    return countryData.categories[selectedCategory] || [];
  }, [countryData, selectedCategory]);

  const handleCountryChange = (e) => {
    const country = e.target.value;
    setSelectedCountry(country);
    setSelectedCategory('');
    if (!country) {
      onSelect?.(null);
    } else if (String(country).trim().toLowerCase() === NONE_COUNTRY.toLowerCase()) {
      onSelect?.({
        country: NONE_COUNTRY,
        category: '',
        organizations: []
      });
    } else {
      onSelect?.(null);
    }
  };

  const handleCategoryChange = (e) => {
    const category = e.target.value;
    setSelectedCategory(category);
    if (category && selectedCountry && !isNoneSelected) {
      const data = charityData.find(c => c.country === selectedCountry);
      const charities = data?.categories[category] || [];
      onSelect?.({
        country: selectedCountry,
        flag: data?.flag,
        category,
        organizations: charities
      });
    } else {
      onSelect?.(null);
    }
  };

  return (
    <div style={styles.wrapper}>
      <div style={styles.header}>
        <div>
          <h3 style={styles.title}>Support a MENA Charity <span style={{ color: '#FFF' }}>*</span></h3>
          <p style={styles.subtitle}>
            <strong>5%</strong> of your purchase supports a charity of your choice, or you can select None
          </p>
        </div>
      </div>

      <div style={styles.body}>
        <div style={styles.field}>
          <label style={styles.label}>Choose a Country</label>
          <select
            value={selectedCountry}
            onChange={handleCountryChange}
            style={styles.select}
          >
            <option value="">Select a country...</option>
            {charityData.map((c) => (
              <option key={c.country} value={c.country}>
                {c.country}
              </option>
            ))}
            <option value={NONE_COUNTRY}>{NONE_COUNTRY}</option>
          </select>
        </div>

        {selectedCountry && (
          <div style={styles.field}>
            <label style={styles.label}>Choose a Category</label>
            <select
              value={selectedCategory}
              onChange={handleCategoryChange}
              disabled={isNoneSelected}
              style={styles.select}
            >
              <option value="">{isNoneSelected ? 'No category needed for None' : 'Select a category...'}</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        )}

        {orgs.length > 0 && !isNoneSelected && (
          <div style={styles.orgsContainer}>
            <div style={styles.orgsHeader}>
              <span style={styles.orgsBadge}>{selectedCountry}</span>
              <span style={styles.categoryBadge}>{selectedCategory}</span>
            </div>
            {orgs.map((org, idx) => (
              <div key={idx} style={styles.orgCard}>
                <div style={styles.orgInfo}>
                  <span style={styles.orgName}>{org.name}</span>
                  {org.url && (
                    <a
                      href={org.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={styles.orgLink}
                    >
                      Visit website
                    </a>
                  )}
                </div>
              </div>
            ))}
            <div style={styles.thankYou}>
              Thank you for making a difference!
            </div>
          </div>
        )}

        {/* No preference button removed - users must choose a charity */}
      </div>
    </div>
  );
};

const styles = {
  wrapper: {
    background: '#fff',
    borderRadius: '12px',
    border: '1px solid #e5e7eb',
    overflow: 'hidden',
    marginBottom: '1.5rem',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  },
  header: {
    padding: '16px 20px',
    background: '#16a34a',
  },
  title: {
    margin: 0,
    fontSize: '1.05rem',
    fontWeight: '700',
    color: '#fff',
    fontFamily: "'Georgia', serif",
  },
  subtitle: {
    margin: '4px 0 0',
    fontSize: '0.82rem',
    color: 'rgba(255,255,255,0.85)',
  },
  body: {
    padding: '20px',
  },
  field: {
    marginBottom: '14px',
  },
  label: {
    display: 'block',
    fontSize: '0.85rem',
    fontWeight: '600',
    color: '#333',
    marginBottom: '6px',
  },
  select: {
    width: '100%',
    padding: '10px 14px',
    fontSize: '0.95rem',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    backgroundColor: '#fff',
    color: '#1a1a1a',
    cursor: 'pointer',
    appearance: 'auto',
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  orgsContainer: {
    marginTop: '16px',
    padding: '16px',
    backgroundColor: '#fafafa',
    borderRadius: '10px',
    border: '1px solid #e5e7eb',
  },
  orgsHeader: {
    display: 'flex',
    gap: '8px',
    marginBottom: '14px',
    flexWrap: 'wrap',
  },
  orgsBadge: {
    display: 'inline-block',
    padding: '4px 12px',
    backgroundColor: '#f0fdf4',
    borderRadius: '20px',
    fontSize: '0.82rem',
    fontWeight: '600',
    color: '#166534',
  },
  categoryBadge: {
    display: 'inline-block',
    padding: '4px 12px',
    backgroundColor: '#f3f4f6',
    borderRadius: '20px',
    fontSize: '0.82rem',
    fontWeight: '600',
    color: '#374151',
  },
  orgCard: {
    padding: '12px 14px',
    backgroundColor: '#fff',
    borderRadius: '8px',
    marginBottom: '8px',
    border: '1px solid #e5e7eb',
  },
  orgInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  orgName: {
    fontWeight: '600',
    fontSize: '0.92rem',
    color: '#1a1a1a',
  },
  orgLink: {
    fontSize: '0.78rem',
    color: '#16a34a',
    textDecoration: 'none',
    fontWeight: '500',
  },
  thankYou: {
    textAlign: 'center',
    fontSize: '0.82rem',
    color: '#16a34a',
    fontWeight: '600',
    marginTop: '12px',
    padding: '8px',
    backgroundColor: '#f0fdf4',
    borderRadius: '8px',
  },
  noPreferenceBtn: {
    width: '100%',
    padding: '12px',
    background: 'none',
    border: '2px dashed #d1d5db',
    borderRadius: '8px',
    color: '#6b7280',
    fontSize: '0.88rem',
    cursor: 'pointer',
    fontWeight: '500',
    transition: 'all 0.2s',
    marginTop: '8px',
  },
};

export default CharitySelector;
